/**
 * NS Assistant's avatar: the visual focus of the conversation.
 *
 * The portrait is a built-in illustration, so the page needs no external asset
 * or avatar service. To use an approved image instead, add it under
 * `src/assets/` and pass it as `imageSrc` (see ASSISTANT_AVATAR_SRC in
 * BusinessAssessmentPage.jsx). The rings and their motion stay the same.
 *
 * `state` is one of: connecting, listening, thinking, speaking, idle, paused.
 * The avatar only animates; the page always pairs it with a text label, so the
 * state never depends on colour or motion alone.
 */

export default function AssistantAvatar({ state = 'idle', imageSrc = null, size = 'large' }) {
  return (
    <div
      className={`bd-avatar bd-avatar--${state} bd-avatar--${size}`}
      role="img"
      aria-label="NS Assistant"
    >
      <span className="bd-avatar__ring bd-avatar__ring--outer" aria-hidden="true" />
      <span className="bd-avatar__ring bd-avatar__ring--inner" aria-hidden="true" />
      <div className="bd-avatar__portrait" aria-hidden="true">
        {imageSrc ? <img src={imageSrc} alt="" className="bd-avatar__image" /> : <Portrait />}
      </div>
    </div>
  );
}

/** A clean, abstract head-and-shoulders portrait on a navy ground. */
function Portrait() {
  return (
    <svg viewBox="0 0 160 160" className="bd-avatar__svg" focusable="false">
      <defs>
        <linearGradient id="bd-avatar-ground" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2563eb" />
          <stop offset="100%" stopColor="#0b2559" />
        </linearGradient>
        <linearGradient id="bd-avatar-figure" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5f8fe" />
          <stop offset="100%" stopColor="#cbd9f2" />
        </linearGradient>
      </defs>
      <rect width="160" height="160" fill="url(#bd-avatar-ground)" />
      <circle cx="80" cy="80" r="64" fill="none" stroke="#ffffff" strokeOpacity="0.08" strokeWidth="1.5" />
      <rect x="71" y="90" width="18" height="22" rx="6" fill="#dbe5f6" />
      <path d="M26 164 C 28 126, 52 108, 80 108 C 108 108, 132 126, 134 164 Z" fill="url(#bd-avatar-figure)" />
      <path d="M67 109.5 L80 128 L93 109.5 C 89 108.5, 84.5 108, 80 108 C 75.5 108, 71 108.5, 67 109.5 Z" fill="#1e40af" />
      <circle cx="80" cy="68" r="25" fill="url(#bd-avatar-figure)" />
    </svg>
  );
}
